<script setup>
import OxdLabel from '@ohrm/oxd/core/components/Label/Label.vue';
import MultiChips from '@ohrm/oxd/core/components/Input/MultiSelect/MultiSelectChips.vue';
import FileInput from '../../../orangehrmPimPlugin/components/FileInput.vue';
import FileUploadInput from '@/core/components/inputs/FileUploadInput';
import TrainingTypes from '../../constants/TrainingTypes';
import {APIService} from '@/core/util/services/api.service';
import {digitsOnlyWithDecimalPoint} from '@ohrm/core/util/validation/rules';
import {defineProps, ref, computed} from 'vue';
import CheckboxInput from '@ohrm/oxd/core/components/Input/CheckboxInput.vue';
import useToast from '@/core/util/composable/useToast';
import Tooltip from '@/core/components/inputs/IconButton';

const props = defineProps({
  id: {
    type: Number,
    default() {
      return null;
    },
  },
  participants: {
    type: Array,
    default() {
      return [];
    },
  },
  filesParticipants: {
    type: Array,
    default() {
      return [];
    },
  },
  training: {
    type: Object,
    default() {
      return {};
    },
  },
});

const files = ref(props.filesParticipants);
const isLoading = ref(false);
const notFirstPageSubmit = ref(false);

const {updateSuccess, saveSuccess, success} = useToast();

// Http Api
const http = new APIService(window.appGlobal.baseUrl, 'api');

const isEdit = computed(() => {
  return (
    props.participants.filter(participant => participant.edit === true).length >
    0
  );
});

const isExternal = computed(() => {
  return TrainingTypes[1].id == props.training.training_type;
});

// Methods
const addAttachment = event => {
  const id = crypto.randomUUID();
  const file = event.target.files[0];
  const fileName = file.name;
  files.value.push({
    id: id,
    label: fileName,
    version: '',
    value: file,
  });
};

const removeAttachment = id => {
  const index = files.value.findIndex(f => f.id === id);

  files.value.splice(index, 1);
};

const onSave = () => {
  isLoading.value = true;

  let url = `api/training-participants/${props.id}/participants`;

  const formFiles = files.value.map(item => {
    return item.value;
  });

  const formData = new FormData();
  formFiles.forEach(file => {
    formData.append('files[]', file);
  });

  props.participants.forEach(participant => {
    if (isExternal.value && participant.certification) {
      formData.append('certifications[]', participant.certification);
      participant.certification = true;
    } else participant.certification = false;

    formData.append('participants[]', JSON.stringify(participant));
  });

  if (isEdit.value || notFirstPageSubmit.value) url = `${url}?_method=PUT`;

  http
    .request({
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      method: 'POST',
      url: url,
      data: formData,
    })
    .then(() => {
      isLoading.value = false;
      if (isEdit.value || notFirstPageSubmit.value) updateSuccess();
      else saveSuccess();
      notFirstPageSubmit.value = true;
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const generateCertification = participant => {
  window.open(
    `${window.appGlobal.publicPath}/api/training/${props.training.encrypted_training_id}/pdf/${participant.encrypted_employee_id}`,
  );
};

const openFile = path => {
  window.open(`${window.appGlobal.publicPath}/${path}`, '_blank');
};

const notifyParticipant = async (employeeId) => {
  isLoading.value = true;
  await http
    .request({
      method: 'POST',
      url: `api/training/attendance/notify/${props.training.id}`,
      data: {
        employeeId: employeeId,
      },
    })
    .then(() => {
      success({title: 'Success', message: 'Notification send to user'});
    })
    .finally(() => {
      isLoading.value = false;
    });
};
</script>
<template>
  <oxd-form :loading="isLoading" @submitValid="onSave">
    <oxd-text tag="h6">
      Attendances, Assessement and Relevance
    </oxd-text>
    <br />
    <table class="w-100">
      <thead>
        <tr>
          <th class="td-style" rowspan="2">
            <OxdLabel
              style="font-weight: 800; font-size: initial;"
              label="Participants"
            />
          </th>
          <th class="td-style" rowspan="2">
            <OxdLabel
              style="font-weight: 800; font-size: initial;"
              label="Attendances"
            />
          </th>
          <th v-if="training.assesment_required" class="td-style" colspan="2">
            <OxdLabel
              style="font-weight: 800; font-size: initial;"
              label="Assessement"
            />
          </th>
          <th class="td-style" rowspan="2">
            <OxdLabel
              style="font-weight: 800; font-size: initial;"
              label="Relevant for the job"
            />
          </th>
          <th class="td-style" rowspan="2">
            <OxdLabel
              style="font-weight: 800; font-size: initial;"
              label="Comments"
            />
          </th>
          <th class="td-style" rowspan="2">
            <OxdLabel
              style="font-weight: 800; font-size: initial;"
              label="Certification"
            />
          </th>
        </tr>
        <tr>
          <th v-if="training.assesment_required" class="td-style">
            <OxdLabel
              style="font-weight: 800; font-size: initial;"
              label="Effectiveness"
            />
          </th>
          <th v-if="training.assesment_required" class="td-style">
            <OxdLabel
              style="font-weight: 800; font-size: initial;"
              label="Score"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="participant in participants" :key="participant.id">
          <td class="text-center">
            <OxdLabel :label="participant.name" />
          </td>
          <td class="text-center">
            <template v-for="presence in participant.presences" :key="presence">
              <span class="tag">
                {{ presence }}
              </span>
              <br />
            </template>
            <template v-if="participant.presences.length < training.training_schedule.length && training.training_schedule.length > 0">
              <Tooltip
              style="margin-top: 10px; margin-bottom: 10px;" 
              name='envelope-fill'
              title='Notify for info'
              @click="notifyParticipant(participant.id)"
             />     
            </template>
          </td>
          <td v-if="training.assesment_required">
            <CheckboxInput
              v-model="participant.effectiveness"
              :checked="participant.effectiveness"
            />
          </td>
          <td v-if="training.assesment_required">
            <oxd-input-field v-model="participant.score" />
          </td>
          <td>
            <CheckboxInput
              v-model="participant.relevante_job"
              :checked="participant.relevante_job"
            />
          </td>
          <td class="text-center">
            <OxdLabel :label="participant.comment" />
          </td>
          <td class="text-center" style="padding: 10px;">
            <template v-if="isExternal && participant.presences?.length > 0">
              <div
                v-for="certification in participant.certifications"
                :key="certification"
                class="flex gap-3 items-center"
              >
                <oxd-icon-button
                  class="org-action-icon"
                  name="download"
                  @click="openFile(certification)"
                />
                <OxdLabel label="Uploaded File" />
              </div>
              <br>
               <file-input
                v-model="participant.certification"
                :is-single="true"
                :has-label="true"
                placeholder="Upload/Replace File"
                label="Upload/Replace File"
              />
            </template>
            <oxd-icon-button
              v-else-if="participant.presences?.length > 0"
              class="org-action-icon"
              name="download"
              @click="generateCertification(participant)"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <br />

    <oxd-input-field
      :value="training.evaluationMethod"
      label="Evaluation Method"
      placeholder="Evaluation Method"
      :disabled="true"
    />

    <oxd-text tag="h6">
      Attendance sheet
    </oxd-text>
    <br />
    <oxd-form-row>
      <oxd-grid :cols="2" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <div class="oxd-input-group">
            <file-input @change="addAttachment" />
          </div>
        </oxd-grid-item>
        <oxd-grid-item class="flex items-center">
          <MultiChips
            :selected="files"
            @chipRemoved="file => removeAttachment(file.id)"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>
    <oxd-divider />
    <oxd-form-actions>
      <submit-button />
    </oxd-form-actions>
  </oxd-form>
</template>
<style scoped>
.w-100 {
  width: 100%;
}

.w-20 {
  width: 20%;
}

.gap-3 {
  gap: 3px;
}

table,
th,
td {
  border-collapse: collapse;
}

tr > td {
  height: 55px;
  border: 1px solid #e8eaef !important;
}

.align-center {
  align-items: center;
}

.text-center {
  text-align: center !important;
}

.td-style {
  border: 1px solid #e8eaef !important;
  padding: 10px !important;
  text-align: center !important;
}

.td-no-pad-style {
  border: 1px solid #e8eaef !important;
  padding: 0px !important;
  text-align: center !important;
}

.oxd-icon-button {
  display: unset;
}

.tag {
  font-family: 'Nunito Sans';
  font-size: 9px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 0.85rem;
  margin-left: 10px;
  margin-right: 10px;
  color: #929baa;
  background-color: #f8f9fa;
}

.oxd-input-field-bottom-space {
  margin-bottom: 0 !important;
}

.oxd-checkbox-wrapper {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}
</style>
